import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import { apiPost } from "../../shared/apiClient";
import { ATTRIBUTES_API, deleteAttribute, fetchAttributeValuesListView } from "../../shared/catalogMastersApi";
import { FormModalShell } from "../../shared/FormModalShell";
import { IconClose, IconEye, IconPlus, IconSave } from "../../shared/toolbarIcons";
import { RecordViewDrawer } from "../../shared/RecordDrawer";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "../../shared/useClientPagination";

function summarizeValuesForAttribute(attributeId, allValues) {
  const rows = allValues
    .filter((v) => Number(v.attributeId) === Number(attributeId))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  if (!rows.length) {
    return { value: "—", colorCode: "—", order: "—" };
  }
  const value = rows.map((r) => (r.value ?? "").trim() || "—").join(", ");
  const colorParts = rows.map((r) => (r.colorCode ?? "").trim()).filter(Boolean);
  const colorCode = colorParts.length ? colorParts.join(", ") : "—";
  const order = rows.map((r) => r.order ?? 0).join(", ");
  return { value, colorCode, order };
}

const emptyForm = () => ({
  name: "",
  slug: "",
  isFilterable: false,
  isVarient: false
});

function formatBool(value) {
  return value ? "Yes" : "No";
}

export function AttributesList() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [attributeValues, setAttributeValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [form, setForm] = useState(() => emptyForm());
  const [formError, setFormError] = useState(null);
  const [viewRow, setViewRow] = useState(null);

  const rows = useMemo(() => {
    return items.map((x) => {
      const summary = summarizeValuesForAttribute(x.id, attributeValues);
      return {
        id: x.id,
        attribute: x,
        valueSummary: summary.value,
        colorCodeSummary: summary.colorCode,
        orderSummary: summary.order
      };
    });
  }, [items, attributeValues]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const result = await fetchAttributeValuesListView();
    if (!result.ok) {
      setError(result.error ?? "Failed to load data");
      setItems([]);
      setAttributeValues([]);
    } else {
      setItems(result.attributes);
      setAttributeValues(result.values);
    }
    setLoading(false);
  }, []);

  const handleDeleteClick = useCallback(
    async (id) => {
      if (!(await window.confirmDialog("Delete this attribute?"))) return;
      const res = await deleteAttribute(id);
      if (!res.ok) setError(res.error ?? "Failed to delete attribute");
      else await load();
    },
    [load]
  );

  const closeCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
    setForm(emptyForm());
    setFormError(null);
  }, []);

  const handleCreateSubmit = async (event) => {
    event.preventDefault();
    if (!form.name.trim()) {
      setFormError("Name is required.");
      return;
    }

    setSaving(true);
    setFormError(null);

    const res = await apiPost(ATTRIBUTES_API, {
      id: 0,
      name: form.name.trim(),
      slug: form.slug || null,
      isFilterable: form.isFilterable,
      isVarient: form.isVarient
    });

    if (!res.ok) {
      setFormError(res.error ?? "Failed to save attribute");
      setSaving(false);
      return;
    }

    closeCreateModal();
    await load();
    setSaving(false);
  };

  const columns = useMemo(
    () => [
      {
        field: "id",
        headerName: "Id",
        width: 72,
        valueGetter: (_v, row) => row.attribute.id
      },
      {
        field: "name",
        headerName: "Name",
        flex: 0.85,
        minWidth: 110,
        valueGetter: (_v, row) => row.attribute.name ?? "—"
      },
      {
        field: "slug",
        headerName: "Slug",
        flex: 0.8,
        minWidth: 100,
        valueGetter: (_v, row) => row.attribute.slug ?? "—"
      },
      {
        field: "valueSummary",
        headerName: "Value",
        flex: 1,
        minWidth: 120,
        valueGetter: (_v, row) => row.valueSummary
      },
      {
        field: "colorCodeSummary",
        headerName: "Color Code",
        flex: 0.9,
        minWidth: 100,
        valueGetter: (_v, row) => row.colorCodeSummary
      },
      {
        field: "orderSummary",
        headerName: "Order",
        width: 90,
        valueGetter: (_v, row) => row.orderSummary
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 160,
        align: "center",
        headerAlign: "center",
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          const x = params.row.attribute;
          return (
            <div className="table-actions">
              <button className="icon-button" type="button" title="View" onClick={() => setViewRow(params.row)}>
                <IconEye />
              </button>
              <button className="icon-button" type="button" title="Edit" onClick={() => navigate(`/attributes?edit=${x.id}`)}>
                ✏️
              </button>
              <button className="icon-button danger" type="button" title="Delete" onClick={() => void handleDeleteClick(x.id)}>
                🗑️
              </button>
            </div>
          );
        }
      }
    ],
    [navigate, handleDeleteClick]
  );

  useEffect(() => {
    void load();
  }, [load]);

  const total = rows.length;

  return (
    <div className="card">
      <div className="card-header card-header--row">
        <div>
          <h2>Attributes</h2>
          <p>{loading ? "Loading attributes..." : `Total: ${total}`}</p>
        </div>
        <div className="card-header-actions">
          <button
            className="icon-button icon-button--toolbar"
            type="button"
            title="Add attribute"
            aria-label="Add attribute"
            onClick={() => setIsCreateModalOpen(true)}
          >
            <IconPlus />
          </button>
        </div>
      </div>
      <div className="card-body">
        {error && <div className="error-banner">{error}</div>}
        <div className="masters-data-grid-shell">
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            pagination
            paginationMode="client"
            pageSizeOptions={DEFAULT_PAGE_SIZE_OPTIONS}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: DEFAULT_PAGE_SIZE_OPTIONS[0] } }
            }}
            sx={{
              border: "none",
              fontFamily: "inherit",
              fontSize: "0.9rem",
              height: "100%",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#f2fcf5",
                borderBottom: "1px solid #e8ecea",
                fontWeight: 600
              },
              "& .MuiDataGrid-footerContainer": {
                borderTop: "1px solid #e8ecea",
                backgroundColor: "#fbfffc"
              },
              "& .MuiDataGrid-row:nth-of-type(even):not(.MuiDataGrid-row--editing):not(:hover)": {
                backgroundColor: "#fcfffd"
              },
              "& .MuiDataGrid-cell": {
                borderBottomColor: "#e8ecea"
              }
            }}
          />
        </div>
      </div>
      <FormModalShell
        open={isCreateModalOpen}
        title="Add Attribute"
        subtitle="Create a new attribute."
        onClose={closeCreateModal}
        actions={
          <>
            <button
              type="button"
              className="icon-button icon-button--toolbar"
              onClick={closeCreateModal}
              disabled={saving}
              title="Cancel"
              aria-label="Cancel"
            >
              <IconClose />
            </button>
            <button
              type="submit"
              form="attribute-create-modal-form"
              className="icon-button icon-button--toolbar"
              disabled={saving}
              title={saving ? "Saving" : "Save"}
              aria-label={saving ? "Saving" : "Save"}
            >
              <IconSave />
            </button>
          </>
        }
      >
        <form id="attribute-create-modal-form" onSubmit={handleCreateSubmit} className="form form-fields-bottomline">
          <div className="form-row form-row--4">
            <label className="field">
              <span>Name</span>
              <input type="text" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} />
            </label>
            <label className="field">
              <span>Slug</span>
              <input type="text" value={form.slug} onChange={(e) => setForm((p) => ({ ...p, slug: e.target.value }))} />
            </label>
          </div>
          {formError && <div className="error-banner">{formError}</div>}
        </form>
      </FormModalShell>
      <RecordViewDrawer
        open={Boolean(viewRow)}
        onClose={() => setViewRow(null)}
        label="Attribute details"
        title={viewRow?.attribute?.name ?? "—"}
        subtitle={viewRow?.attribute?.slug}
        ariaLabel="Attribute details"
        sections={
          viewRow
            ? [
                {
                  title: "Attribute Details",
                  columns: 2,
                  fields: [
                    ["Id", viewRow.attribute.id],
                    ["Name", viewRow.attribute.name ?? "—"],
                    ["Slug", viewRow.attribute.slug ?? "—"],
                    ["Filterable", formatBool(viewRow.attribute.isFilterable)],
                    ["Variant", formatBool(viewRow.attribute.isVarient)]
                  ]
                },
                {
                  title: "Values Summary",
                  columns: 2,
                  fields: [
                    ["Values", viewRow.valueSummary],
                    ["Color Codes", viewRow.colorCodeSummary],
                    ["Order", viewRow.orderSummary]
                  ]
                }
              ]
            : []
        }
      />
    </div>
  );
}
